import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  BookOpen,
  Search,
  Menu,
  X,
  ChevronRight,
  Home,
  ArrowLeft,
  Users,
  Sparkles,
  CreditCard,
  Building2,
  Shield,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PRACTITIONER_GUIDES, type PractitionerGuide } from '@/constants/practitionerGuides';

interface TOCItem {
  id: string;
  title: string;
  level: number;
}

const iconMap: Record<string, React.ElementType> = {
  Users,
  Sparkles,
  CreditCard,
  Building2,
  Shield,
};

const PractitionerResourcesPage = () => {
  const [activeGuideId, setActiveGuideId] = useState(PRACTITIONER_GUIDES[0].id);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');

  const activeGuide = PRACTITIONER_GUIDES.find((g) => g.id === activeGuideId) || PRACTITIONER_GUIDES[0];

  // Extract table of contents from markdown
  const tableOfContents = useMemo(() => {
    const lines = activeGuide.content.split('\n');
    const toc: TOCItem[] = [];

    lines.forEach((line) => {
      const match = line.match(/^(#{1,3})\s+(.+)$/);
      if (match) {
        const level = match[1].length;
        const title = match[2].trim();
        const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        toc.push({ id, title, level });
      }
    });

    return toc;
  }, [activeGuide.content]);

  // Filter content based on search
  useEffect(() => {
    if (!searchQuery) return;

    const searchLower = searchQuery.toLowerCase();
    const firstMatch = tableOfContents.find((item) =>
      item.title.toLowerCase().includes(searchLower)
    );

    if (firstMatch) {
      const element = document.getElementById(firstMatch.id);
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [searchQuery, tableOfContents]);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents
        .map((item) => ({
          id: item.id,
          element: document.getElementById(item.id),
        }))
        .filter((s) => s.element);

      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.element && section.element.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tableOfContents]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  // Custom markdown components to add IDs to headings
  const components = {
    h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => {
      const id = String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-');
      return (
        <h1 id={id} className="scroll-mt-20" {...props}>
          {children}
        </h1>
      );
    },
    h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => {
      const id = String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-');
      return (
        <h2 id={id} className="scroll-mt-20" {...props}>
          {children}
        </h2>
      );
    },
    h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => {
      const id = String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-');
      return (
        <h3 id={id} className="scroll-mt-20" {...props}>
          {children}
        </h3>
      );
    },
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-4">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-primary" />
            <h1 className="text-xl font-bold">Practitioner Resources</h1>
          </div>

          <div className="flex items-center gap-2">
            <Link to="/practitioner">
              <Button variant="outline" size="sm" className="rounded-full">
                <Home className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Dashboard</span>
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search practitioner guides..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Guide Selection Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {PRACTITIONER_GUIDES.map((guide) => {
            const Icon = iconMap[guide.icon] || BookOpen;
            const isActive = activeGuideId === guide.id;
            return (
              <Card
                key={guide.id}
                className={`cursor-pointer transition-all hover:shadow-lg ${
                  isActive ? 'ring-2 ring-primary shadow-lg' : ''
                }`}
                onClick={() => {
                  setActiveGuideId(guide.id);
                  setSearchQuery('');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isActive ? 'bg-primary/10' : 'bg-muted'}`}>
                      <Icon
                        className={`h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm mb-1 flex items-center justify-between">
                        {guide.title}
                        {isActive && <ChevronRight className="h-4 w-4 text-primary" />}
                      </h3>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {guide.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Table of Contents - Desktop */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24">
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Menu className="h-4 w-4" />
                    On This Page
                  </h3>
                  <ScrollArea className="h-[calc(100vh-16rem)]">
                    <nav className="space-y-1">
                      {tableOfContents.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className={`block w-full text-left px-2 py-1.5 text-sm rounded transition-colors ${
                            activeSection === item.id
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                          }`}
                          style={{ paddingLeft: `${(item.level - 1) * 12 + 8}px` }}
                        >
                          {item.title}
                        </button>
                      ))}
                    </nav>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Table of Contents - Mobile */}
          {mobileMenuOpen && (
            <div className="lg:hidden fixed inset-0 z-40 bg-background/95 backdrop-blur">
              <div className="container h-full py-20 px-4">
                <Card>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-3">On This Page</h3>
                    <ScrollArea className="h-[calc(100vh-12rem)]">
                      <nav className="space-y-1">
                        {tableOfContents.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => scrollToSection(item.id)}
                            className={`block w-full text-left px-2 py-1.5 text-sm rounded transition-colors ${
                              activeSection === item.id
                                ? 'bg-primary/10 text-primary font-medium'
                                : 'text-muted-foreground hover:bg-muted'
                            }`}
                            style={{ paddingLeft: `${(item.level - 1) * 12 + 8}px` }}
                          >
                            {item.title}
                          </button>
                        ))}
                      </nav>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Guide Content */}
          <div className="lg:col-span-3">
            <Card>
              <CardContent className="p-6 md:p-8">
                <div className="prose prose-slate dark:prose-invert max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                    {activeGuide.content}
                  </ReactMarkdown>
                </div>
              </CardContent>
            </Card>

            {/* Navigation Footer */}
            <div className="mt-6 flex justify-between items-center">
              <Button
                variant="outline"
                onClick={() => {
                  const currentIndex = PRACTITIONER_GUIDES.findIndex(
                    (g) => g.id === activeGuideId
                  );
                  if (currentIndex > 0) {
                    setActiveGuideId(PRACTITIONER_GUIDES[currentIndex - 1].id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                disabled={PRACTITIONER_GUIDES.findIndex((g) => g.id === activeGuideId) === 0}
              >
                Previous Guide
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  const currentIndex = PRACTITIONER_GUIDES.findIndex(
                    (g) => g.id === activeGuideId
                  );
                  if (currentIndex < PRACTITIONER_GUIDES.length - 1) {
                    setActiveGuideId(PRACTITIONER_GUIDES[currentIndex + 1].id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                disabled={
                  PRACTITIONER_GUIDES.findIndex((g) => g.id === activeGuideId) ===
                  PRACTITIONER_GUIDES.length - 1
                }
              >
                Next Guide
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PractitionerResourcesPage;
